import type { FC} from 'react';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Container, CookieIcon, Link, Paper, Slide, Stack, Typography } from '@/shared/ui/mui';
import { updateAnalyticsConsent } from '@/app/utils/analytics';
import type { CookieCategories } from '@/shared/ui/cookie-consent';
import { cookieConsentService } from '@/shared/ui/cookie-consent';
import { CookiePreferencesModal } from './CookiePreferencesModal';

export const CookieConsentBanner: FC = () => {
  const navigate = useNavigate();
  const bannerRef = useRef<HTMLDivElement>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const checkConsent = () => {
      // Check if consent is needed
      if (cookieConsentService.needsConsent()) {
        setShowBanner(true);
        return;
      }

      // Apply saved preferences
      const consent = cookieConsentService.getConsent();
      if (consent?.categories.analytics) {
        updateAnalyticsConsent(true);
      }
    };

    // Small delay to prevent layout shift on page load
    setTimeout(checkConsent, 1000);
  }, []);

  // Focus management: trap focus in banner when shown
  useEffect(() => {
    if (showBanner && bannerRef.current) {
      const firstButton = bannerRef.current.querySelector<HTMLButtonElement>('button');
      firstButton?.focus();
    }
  }, [showBanner]);

  // Keyboard navigation
  useEffect(() => {
    if (!showBanner) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // ESC key = Essential Only (reject all)
        handleEssentialOnly();
      }

      // Tab trap
      if (e.key === 'Tab' && bannerRef.current) {
        const focusableElements = bannerRef.current.querySelectorAll<HTMLElement>(
          'button, a, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement?.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement?.focus();
            e.preventDefault();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showBanner]);

  const handleAcceptAll = () => {
    const gpc = cookieConsentService.detectGPC();
    cookieConsentService.acceptAll(gpc);
    updateAnalyticsConsent(true);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const gpc = cookieConsentService.detectGPC();
    cookieConsentService.rejectAll(gpc);
    updateAnalyticsConsent(false);
    setShowBanner(false);
  };

  const handleEssentialOnly = () => {
    const gpc = cookieConsentService.detectGPC();
    cookieConsentService.essentialOnly(gpc);
    updateAnalyticsConsent(false);
    setShowBanner(false);
  };

  const handleManagePreferences = () => {
    setShowPreferences(true);
  };

  const handleSavePreferences = (categories: CookieCategories) => {
    const gpc = cookieConsentService.detectGPC();
    cookieConsentService.saveConsent(categories, gpc);
    updateAnalyticsConsent(categories.analytics);
    setShowBanner(false);
  };

  const handlePrivacyPolicy = () => {
    setShowBanner(false);
    navigate('/privacy');
  };

  if (!showBanner) return null;

  return (
    <>
      <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
        <Paper
          ref={bannerRef}
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-description"
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                py: 3,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: 3,
              }}
            >
              {/* Icon and Text */}
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <CookieIcon
                    sx={{
                      color: 'primary.main',
                      fontSize: 28,
                      mt: 0.5,
                    }}
                    aria-hidden="true"
                  />
                  <Box>
                    <Typography
                      id="cookie-banner-title"
                      variant="h6"
                      gutterBottom
                    >
                      We Value Your Privacy
                    </Typography>
                    <Typography
                      id="cookie-banner-description"
                      variant="body2"
                      color="text.secondary"
                    >
                      We use cookies to enhance your browsing experience, analyze
                      traffic, and improve our services. You can customize your
                      preferences or accept all cookies.{' '}
                      <Link
                        component="button"
                        variant="body2"
                        onClick={handlePrivacyPolicy}
                        sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        Privacy Policy
                      </Link>
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Buttons - Equal Prominence (GDPR 2025 requirement) */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleRejectAll}
                  sx={{
                    minWidth: 140,
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: 'text.primary',
                      backgroundColor: 'action.hover',
                    },
                  }}
                  aria-label="Reject all cookies except essential"
                >
                  Reject All
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleManagePreferences}
                  sx={{
                    minWidth: 140,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'action.hover',
                    },
                  }}
                  aria-label="Manage cookie preferences"
                >
                  Manage Preferences
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAcceptAll}
                  sx={{
                    minWidth: 140,
                    backgroundColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }}
                  aria-label="Accept all cookies"
                >
                  Accept All
                </Button>
              </Stack>
            </Box>
          </Container>
        </Paper>
      </Slide>

      {/* Cookie Preferences Modal */}
      <CookiePreferencesModal
        open={showPreferences}
        onClose={() => setShowPreferences(false)}
        onSave={handleSavePreferences}
      />
    </>
  );
};

// Export hook for checking consent status (backward compatibility)
export const useCookieConsent = () => {
  const consent = cookieConsentService.getConsent();

  return {
    hasConsent: !!consent,
    hasAnalyticsConsent: consent?.categories.analytics ?? false,
    consentData: consent,
  };
};
